import { getManagedCredential } from "../auth-refresh-coordinator"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * Client for a hosted agent's artifact library (HERMES wave 2/3, B4).
 *
 * Artifacts are finished pieces of work a user chose to publish from an
 * agent thread. They live in their own Cosmos container, are versioned per
 * family (`root_id` / `parent_id`) and are visible to everyone who can access
 * the agent once `published` is true. Access is gated by the same uniform-404
 * agent resolution as the thread endpoints — a missing agent and a denied
 * agent are indistinguishable by design.
 */

export type HostedAgentArtifactKind = "markdown" | "text" | "document"

export type HostedAgentArtifactBlobRef = {
  container: string
  path: string
  content_type: string
  filename?: string
}

/** Where the artifact came from: the thread/turn/message it was published from. */
export type HostedAgentArtifactProvenance = {
  session_id?: string
  turn_id?: string
  message_id?: string
}

export type HostedAgentArtifact = {
  /** `artifact:<uuid>` */
  id: string
  /** `agent:<uuid>` — the agent whose library this belongs to. */
  agent_id: string
  title: string
  kind: HostedAgentArtifactKind
  /** Present for `markdown`/`text`; `null` for `document` (see `blob_ref`). */
  content: string | null
  blob_ref: HostedAgentArtifactBlobRef | null
  /** 1-based; the server computes `parent.version + 1` from `parent_id`. */
  version: number
  /** Artifact family id — equals `id` on v1. */
  root_id: string
  parent_id: string | null
  /** Author oid. Only the author (or an admin) may delete/unpublish. */
  created_by: string
  created_by_name: string
  created_at: string
  updated_at: string
  provenance: HostedAgentArtifactProvenance
  tags: string[]
  published: boolean
  summary: string
  /**
   * Set when an admin unpublished the version (sticky moderation): the author
   * cannot republish or continue it until an admin lifts the lock.
   */
  moderated_at?: string | null
}

/**
 * Agent-proposed candidate carried in the run context (`artifact_candidate`)
 * when the `propose_artifact` tool ran during the turn. Never published on
 * its own — the user confirms.
 */
export type HostedAgentArtifactCandidate = {
  title: string
  kind: HostedAgentArtifactKind
  summary: string
}

export type CreateHostedAgentArtifactRequest = {
  title: string
  kind: HostedAgentArtifactKind
  content: string
  summary?: string
  tags?: string[]
  provenance?: HostedAgentArtifactProvenance
  /** Previous version's id → the server creates vN+1 in that family. */
  parent_id?: string
}

export type UpdateHostedAgentArtifactRequest = {
  title?: string
  summary?: string
  tags?: string[]
  published?: boolean
}

export type HostedAgentArtifactWithFamily = {
  artifact: HostedAgentArtifact
  /** Every version of the family, newest first. */
  versions: HostedAgentArtifact[]
}

type ArtifactListPayload = { artifacts?: HostedAgentArtifact[] } | HostedAgentArtifact[]
type ArtifactPayload = { artifact?: HostedAgentArtifact } | HostedAgentArtifact
type ArtifactFamilyPayload = {
  artifact?: HostedAgentArtifact
  versions?: HostedAgentArtifact[]
  family?: HostedAgentArtifact[]
}

function artifactsUrl(slug: string, artifactId?: string): string {
  const base = `${BACKEND_URI}/agents/${encodeURIComponent(slug)}/artifacts`
  return artifactId ? `${base}/${encodeURIComponent(artifactId)}` : base
}

function unwrapArtifact(payload: ArtifactPayload): HostedAgentArtifact {
  return "artifact" in payload && payload.artifact
    ? payload.artifact
    : (payload as HostedAgentArtifact)
}

/**
 * QUERY: Lists the artifacts visible to the caller for one agent — published
 * ones plus the caller's own unpublished drafts. One row per family (latest version).
 * @category Query
 */
export async function listHostedAgentArtifactsApi(
  slug: string,
  idToken?: string
): Promise<HostedAgentArtifact[]> {
  const response = await fetchWithAuthRetry(
    artifactsUrl(slug),
    { method: "GET", headers: getHeaders(idToken ?? getManagedCredential()) },
    idToken
  )
  const payload = (await response.json()) as ArtifactListPayload
  if (Array.isArray(payload)) return payload
  return payload.artifacts ?? []
}

/**
 * QUERY: One artifact plus its version family (`?family=1`).
 * @category Query
 */
export async function getHostedAgentArtifactApi(
  slug: string,
  artifactId: string,
  idToken?: string
): Promise<HostedAgentArtifactWithFamily> {
  const response = await fetchWithAuthRetry(
    `${artifactsUrl(slug, artifactId)}?family=1`,
    { method: "GET", headers: getHeaders(idToken ?? getManagedCredential()) },
    idToken
  )
  const payload = (await response.json()) as ArtifactFamilyPayload
  const artifact = payload.artifact ?? (payload as unknown as HostedAgentArtifact)
  const versions = payload.versions ?? payload.family ?? [artifact]
  return { artifact, versions }
}

/**
 * MUTATION: Publishes a new artifact (v1) or, with `parent_id`, the next
 * version in an existing family.
 * @category Mutation
 */
export async function createHostedAgentArtifactApi(
  slug: string,
  body: CreateHostedAgentArtifactRequest,
  idToken?: string
): Promise<HostedAgentArtifact> {
  const response = await fetchWithAuthRetry(
    artifactsUrl(slug),
    {
      method: "POST",
      headers: getHeaders(idToken ?? getManagedCredential()),
      body: JSON.stringify(body),
    },
    idToken
  )
  return unwrapArtifact((await response.json()) as ArtifactPayload)
}

/**
 * MUTATION: Author-only metadata update (title, tags, published flag).
 * @category Mutation
 */
export async function updateHostedAgentArtifactApi(
  slug: string,
  artifactId: string,
  body: UpdateHostedAgentArtifactRequest
): Promise<HostedAgentArtifact> {
  const response = await fetchWithAuthRetry(artifactsUrl(slug, artifactId), {
    method: "PATCH",
    headers: getHeaders(getManagedCredential()),
    body: JSON.stringify(body),
  })
  return unwrapArtifact((await response.json()) as ArtifactPayload)
}

/**
 * MUTATION: Author-only delete of one version.
 * @category Mutation
 */
export async function deleteHostedAgentArtifactApi(
  slug: string,
  artifactId: string,
  idToken?: string
): Promise<void> {
  await fetchWithAuthRetry(
    artifactsUrl(slug, artifactId),
    { method: "DELETE", headers: getHeaders(idToken ?? getManagedCredential()) },
    idToken
  )
}

/** The artifact endpoint checks its audience before reading the stored blob. */
export async function downloadHostedAgentArtifact(slug: string, artifactId: string): Promise<Blob> {
  return (
    await fetchWithAuthRetry(`${artifactsUrl(slug, artifactId)}/download`, {
      headers: getHeaders(getManagedCredential()),
    })
  ).blob()
}
